import { compile, computed, markRaw, ref, resolveComponent, shallowRef, triggerRef, watch } from "vue";
import { RBTree } from "@/util/RedBlackTree";
import { useLogStore } from "@/stores/LogStore";
import { useToastStore } from "@/stores/ToastStore";
import { useChartStore } from "@/stores/ChartStore";
import { useSelectionStore } from "@/stores/SelectionStore";
import { EventLineType } from "@/util/constants";

export function useChart() {
  // --------------------------------------------------------
  // CONSTANTS 
  // --------------------------------------------------------

  const chartStore = useChartStore()
  const selectionStore = useSelectionStore()

  // --------------------------------------------------------
  // STATE
  // --------------------------------------------------------

  // parameters
  const chartParameters = ref({})

  // chart
  const eventLines = shallowRef(new Map()) // Map<eventLineId, {eventLineId, time, type, colour, thickness, ...others}>
  const columns = shallowRef(new Map()) // Map<columnId, [{columnId, columnIndex, name, colour }]>
  const notes = shallowRef(new Map()) // Map<noteId, { noteId, column, eventLine, numerator, denominator, beat }>
  const notesByColumn = new WeakMap() // Map<column, [note]>
  const notesByEventLine = new Map() // Map<eventLineId, RBTree<beat, [note]>>
  const duration = ref(300)


  // --------------------------------------------------------
  // GETTERS 
  // --------------------------------------------------------

  // chart
  const orderedEventLines = computed(() => {
    return Array.from(eventLines.value.values()).sort((a, b) => a.time - b.time)
  })

  const orderedCL = computed(() => {
    const ordered = orderedEventLines.value.filter(e => e.type === EventLineType.Timing)
    const res = []
    let i = 0
    for (; i < ordered.length - 1; i++) {
      res.push({
        eventLine: ordered[i],
        endTime: ordered[i + 1].time,
        durationInBeats: getBeatFromTime(ordered[i + 1].time, ordered[i].eventLineId)
      })
    }
    if (ordered.length > 0)
      res.push({
        eventLine: ordered[i],
        endTime: duration.value,
        durationInBeats: getBeatFromTime(duration.value, ordered[i].eventLineId)
      })
    return res;
  })

  const orderedCols = computed(() => {
    return Array.from(columns.value.values()).sort((a, b) => a.columnIndex - b.columnIndex)
  })

  const getTimeFromBeat = (beat, eventLineId) => {
    const c = eventLines.value.get(eventLineId)
    if (!c || c.type !== EventLineType.Timing || beat < 0) return null;
    return c.time + 60 / c.bpm * beat
  }

  const getBeatFromTime = (time, eventLineId = null) => {
    let c
    if (eventLineId == null)
      c = orderedCL.value.find(e => e.endTime > time).eventLine
    else
      c = eventLines.value.get(eventLineId)

    if (!c || c.type !== EventLineType.Timing || time < 0) return null;
    return (time - c.time) * c.bpm / 60
  }

  const columnCount = computed(() => {
    return columns.value.size
  })

  // --------------------------------------------------------
  // ACTIONS 
  // --------------------------------------------------------

  const setDuration = (value) => {
    duration.value = value <= 0 ? 0 : value
  }

  // chart
  const resetChart = () => {
    notes.value.clear()
    notesByEventLine.clear()
    eventLines.value.clear()
    notesByEventLine.clear()
    columns.value = new Map()
    chartParameters.value = {}
    for (let i = 0; i < 30; i++)
      addChartColumn()

  }

  const setChartParameter = (key, value) => {
    if (value == null) {
      delete chartParameters.value[key]
      return
    }
    chartParameters.value[key] = value
  }

  // chart / event line
  const addEventLine = (time, type = EventLineType.Generic, other = {}) => {
    const eventLineId = chartStore.getNextId()
    if (type === EventLineType.Timing) {
      if (other.bpm == null || other.bpm <= 0) other.bpm = 120
      if (other.colour == null) other.colour = '#FF6600'
      if (other.thickness == null) other.thickness = 6
    }

    if (other.colour == null) other.colour = '#0000FF'
    if (other.thickness == null) other.thickness = 4

    const eventLine = markRaw({ ...other, eventLineId, time, type })
    eventLines.value.set(eventLineId, eventLine)
    notesByEventLine.set(eventLineId, markRaw(new RBTree(notesMergeFunction, notesSubtractFunction)))

    triggerRef(eventLines)
    return eventLine
  }

  const removeEventLine = (eventLineId) => {
    const eventLine = eventLines.value.get(eventLineId)

    if (eventLine.type === EventLineType.Timing) {
      for (const b of notesByEventLine.get(eventLineId).rangeQuery(-Infinity, Infinity)) {
        for (const n of b)
          notes.value.delete(n.noteId)
      }
      notesByEventLine.delete(eventLineId)
      triggerRef(notes)
    }

    eventLines.value.delete(eventLineId)
    triggerRef(eventLines)
  }

  const setEventLineParameter = (eventLineId, key, value) => {
    if (eventLineId == null || key == "eventLineId") return
    const eventLine = eventLines.value.get(eventLineId)

    if (eventLine.type === EventLineType.Timing && key == "bpm" && (value <= 0 || isNaN(value))) return
    if (key == "time") value = isNaN(value) ? 0 : Number(value)
    if (key == "type" && value === EventLineType.Timing && (eventLine.bpm == null || eventLine.bpm == "")) eventLine.bpm = 120

    if (value == null)
      delete eventLine[key]
    else
      eventLine[key] = value

    triggerRef(eventLines)
  }

  // chart / column
  const addChartColumn = () => {
    const columnId = chartStore.getNextId()
    const column = markRaw({ columnId, columnIndex: columnCount.value, name: "", colour: "#FFFFFF" })
    columns.value.set(columnId, column)
    notesByColumn.set(column, [])
    triggerRef(columns)
    return column
  }

  const removeChartColumn = (columnId) => {
    const col = columns.value.get(columnId)
    if (col == null) return

    for (let i = col.columnIndex + 1; i < orderedCols.value.length; i++)
      orderedCols.value[i].columnIndex -= 1

    columns.value.delete(columnId)
    const notes = notesByColumn.get(col)

    for (let i = notes.length - 1; i >= 0; i--) {
      removeNote(notes[i].noteId)
    }

    triggerRef(columns)
  }

  const moveChartColumns = (targetColumns, targetIndex) => {
    targetIndex = Math.max(Math.min(targetIndex, columnCount.value - targetColumns.length), 0)
    if (isNaN(targetIndex)) return


    const indexes = targetColumns.map(e => e.columnIndex)
    const remainingCols = orderedCols.value.filter(e => !indexes.includes(e.columnIndex))
    indexes.forEach((e, ei) => {
      const i = Math.min(remainingCols.length, targetIndex + e - indexes[0])
      remainingCols.splice(i, 0, targetColumns[ei])
    })

    remainingCols.forEach((e, i) => {
      e.columnIndex = i
    })

    triggerRef(columns)
    triggerRef(notes)
  }

  const setColumnParameter = (columnId, key, value) => {
    const col = columns.value.get(columnId)
    if (col == null || key == "columnId" || key == "columnIndex") return

    if (value == null)
      delete col[key]
    else
      col[key] = value
    triggerRef(columns)
  }

  // chart / notes
  const notesMergeFunction = (x, xx) => x.concat(xx)

  const notesSubtractFunction = (a, x) => {
    const i = a.indexOf(x);
    if (i > -1) a.splice(i, 1);
    return a
  }

  const addNote = (eventLine, columnIndex, numerator, denominator, other = {}) => {
    const col = orderedCols.value[columnIndex]
    if (!col || eventLine.type !== EventLineType.Timing) return;

    const gcd = (a, b) => b === 0 ? a : gcd(b, a % b);
    const v = gcd(numerator, denominator)

    numerator /= v
    denominator /= v

    const beat = numerator / denominator
    const notesByConLine = notesByEventLine.get(eventLine.eventLineId)
    const n = notesByConLine.find(beat)

    if (n && n.data.find(e => e.column.columnIndex == columnIndex)) {
      for (const [k, v] of Object.entries(other)) setNoteParameter(n.noteId, k, v)
      return
    }
    const noteId = chartStore.getNextId()
    const note = markRaw({ noteId, column: col, eventLine, numerator, denominator, beat, ...other })

    notesByConLine.insert(beat, [note])
    notesByColumn.get(col).push(note)

    notes.value.set(noteId, note)
    triggerRef(notes)
  }

  const addChord = (eventLine, columnIndexArray, otherParametersArray, numerator, denominator) => {
    if (eventLine.type !== EventLineType.Timing) return;

    const gcd = (a, b) => b === 0 ? a : gcd(b, a % b);
    const v = gcd(numerator, denominator)

    numerator /= v
    denominator /= v
    const beat = numerator / denominator

    const notesByConLine = notesByEventLine.get(eventLine.eventLineId)
    const n = notesByConLine.find(beat)

    const t = []
    for (let i = 0; i < columnIndexArray.length; i++) {
      const colIndex = columnIndexArray[i]
      if (colIndex < 0 || colIndex >= columnCount.value) continue
      if (n && n.data.find(e => e.column.columnIndex == colIndex)) continue
      const noteId = chartStore.getNextId()
      const col = orderedCols.value[colIndex]
      const note = markRaw({ noteId, column: col, eventLine, numerator, denominator, beat, ...otherParametersArray[i] })
      t.push(note)
      notesByColumn.get(col).push(note)
      notes.value.set(noteId, note)
    }

    notesByEventLine.get(eventLine.eventLineId).insert(beat, t)
    triggerRef(notes)
  }

  const removeNote = (noteId) => {
    const note = notes.value.get(noteId)

    if (!note) return

    const notesByCol = notesByColumn.get(note.column)
    if (notesByCol) {
      const i = notesByCol.indexOf(note);
      if (i > -1) notesByCol.splice(i, 1);
    }

    const notesByCl = notesByEventLine.get(note.eventLine.eventLineId)
    if (notesByCl) {
      notesByCl.subtractData(note.beat, note)
    }

    selectionStore.deselectNote(noteId)
    notes.value.delete(noteId)
    triggerRef(notes)
  }

  const setNoteParameter = (noteId, key, value) => {
    if (!noteId) return
    if (["noteId", "column", "eventLine", "numerator", "denominator", "beat"].includes(key)) return
    notes.value.get(noteId)[key] = value
    triggerRef(notes)
  }

  // --------------------------------------------------------
  // MISC 
  // --------------------------------------------------------

  resetChart()
  addEventLine(0, EventLineType.Timing)

  const returnObj = {
    setDuration,
    addChord,
    resetChart,
    addEventLine,
    removeEventLine,
    eventLines,
    columnCount,
    addChartColumn,
    columns,
    notes,
    notesByColumn,
    notesByEventLine,
    getBeatFromTime,
    orderedCL,
    removeChartColumn,
    moveChartColumns,
    addNote,
    removeNote,
    getTimeFromBeat,
    chartParameters,
    setChartParameter,
    setEventLineParameter,
    setColumnParameter,
    setNoteParameter,
    duration,
    orderedEventLines,
    orderedCols,
  }

  return returnObj
}